/**
 * File Parameters Utilities
 * Parameter definitions for CSV file tables in PDF
 */

import type { TFunction } from 'i18next';
import { ParameterDefinition } from '../types';
import { CSVFileEntry } from '@/types/common';

/**
 * Format date value for display
 */
export const formatDate = (val: unknown): string => {
  if (!val) return '-';
  try {
    return new Date(val as string).toLocaleString('de-DE');
  } catch {
    return '-';
  }
};

/**
 * Format date only (without time)
 */
export const formatDateOnly = (val: unknown): string => {
  if (!val) return '-';
  try {
    return new Date(val as string).toLocaleDateString('de-DE');
  } catch {
    return '-';
  }
};

/**
 * Get input file parameters definition
 */
export const getInputFileParameters = (t: TFunction): ParameterDefinition[] => [
  { key: 'bezeichnung', label: t('params.bezeichnung') },
  { key: 'utc_min', label: t('params.utcMin'), format: formatDate },
  { key: 'utc_max', label: t('params.utcMax'), format: formatDate },
  { key: 'zeitschrittweite', label: t('params.stepWidth') },
  { key: 'min', label: t('params.minValue') },
  { key: 'max', label: t('params.maxValue') },
  { key: 'offsett', label: t('params.offset') },
  { key: 'datenpunkte', label: t('params.dataPoints') },
  { key: 'numerische_datenpunkte', label: t('params.numericDataPoints') },
  { key: 'numerischer_anteil', label: t('params.numericShare') },
  { key: 'datenform', label: t('params.shape') },
  { key: 'zeithorizont_start', label: t('params.horizonStart') },
  { key: 'zeithorizont_end', label: t('params.horizonEnd') },
  { key: 'file_name', label: t('params.fileName') },
  { key: 'type', label: t('params.type') },
  { key: 'datenanpassung', label: t('params.dataAdjustment') },
  { key: 'skalierung', label: t('params.scaling') },
  {
    key: 'skalierung_min',
    label: t('params.scalingMin'),
    condition: (file: CSVFileEntry & Record<string, unknown>) => file.skalierung === 'ja',
  },
  {
    key: 'skalierung_max',
    label: t('params.scalingMax'),
    condition: (file: CSVFileEntry & Record<string, unknown>) => file.skalierung === 'ja',
  },
  { key: 'zeitschrittweite_transferierten_daten', label: t('params.transferredStepWidth') },
  { key: 'offset_transferierten_daten', label: t('params.transferredOffset') },
  { key: 'mittelwertbildung_uber_den_zeithorizont', label: t('params.averagingOverHorizon') },
  {
    key: 'zeitschrittweite_mittelwert',
    label: t('params.averagingStepWidth'),
    condition: (file: CSVFileEntry & Record<string, unknown>) =>
      (file.mittelwertbildung_uber_den_zeithorizont ||
        (file as Record<string, unknown>).mittelwertbildung_uber_den_zeithorizont) === 'ja',
  },
  {
    key: 'zeitschrittweite_min',
    label: t('params.averagingMinStep'),
    condition: (file: CSVFileEntry & Record<string, unknown>) =>
      (file.mittelwertbildung_uber_den_zeithorizont ||
        (file as Record<string, unknown>).mittelwertbildung_uber_den_zeithorizont) === 'ja',
  },
  { key: 'created_at', label: t('params.createdAt'), format: formatDateOnly },
  { key: 'updated_at', label: t('params.updatedAt'), format: formatDateOnly },
];

/**
 * Get output file parameters definition
 */
export const getOutputFileParameters = (
  t: TFunction,
  fileGroup: (CSVFileEntry & Record<string, unknown>)[]
): ParameterDefinition[] => {
  const parameters: ParameterDefinition[] = [
    { key: 'bezeichnung', label: t('params.bezeichnung') },
    { key: 'file_name', label: t('params.fileName') },
    { key: 'type', label: t('params.type') },
    { key: 'utc_min', label: t('params.utcMin'), format: formatDate },
    { key: 'utc_max', label: t('params.utcMax'), format: formatDate },
    { key: 'zeitschrittweite', label: t('params.stepWidth') },
    { key: 'min', label: t('params.minValue') },
    { key: 'max', label: t('params.maxValue') },
    { key: 'offsett', label: t('params.offset') },
    { key: 'datenpunkte', label: t('params.dataPoints') },
    { key: 'numerische_datenpunkte', label: t('params.numericDataPoints') },
    { key: 'numerischer_anteil', label: t('params.numericShare') },
    { key: 'datenform', label: t('params.shape') },
    { key: 'zeithorizont_start', label: t('params.horizonStart') },
    { key: 'zeithorizont_end', label: t('params.horizonEnd') },
    { key: 'zeitschrittweite_transferierten_daten', label: t('params.transferredStepWidth') },
    { key: 'offset_transferierten_daten', label: t('params.transferredOffset') },
    { key: 'mittelwertbildung_uber_den_zeithorizont', label: t('params.averagingOverHorizon') },
    { key: 'datenanpassung', label: t('params.dataAdjustment') },
    { key: 'skalierung', label: t('params.scaling') },
  ];

  // Add conditional parameters
  if (fileGroup.some((file) => file.skalierung === 'ja')) {
    parameters.push(
      { key: 'skalierung_min', label: t('params.scalingMin') },
      { key: 'skalierung_max', label: t('params.scalingMax') }
    );
  }

  if (
    fileGroup.some(
      (file) =>
        (file.mittelwertbildung_uber_den_zeithorizont ||
          (file as Record<string, unknown>).mittelwertbildung_uber_den_zeithorizont) === 'ja'
    )
  ) {
    parameters.push(
      { key: 'zeitschrittweite_mittelwert', label: t('params.averagingStepWidth') },
      { key: 'zeitschrittweite_min', label: t('params.averagingMinStep') }
    );
  }

  parameters.push(
    { key: 'created_at', label: t('params.createdAt'), format: formatDateOnly },
    { key: 'updated_at', label: t('params.updatedAt'), format: formatDateOnly }
  );

  return parameters;
};

/**
 * Group files into chunks for pagination
 */
export const groupFilesForPagination = <T>(
  files: T[],
  maxPerPage: number = 3
): T[][] => {
  const groups: T[][] = [];
  for (let i = 0; i < files.length; i += maxPerPage) {
    groups.push(files.slice(i, i + maxPerPage));
  }
  return groups;
};
